import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, Lock, Eye, EyeOff, CheckCircle2, 
  AlertTriangle, Info, Settings, Save
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface ConsentSettings {
  share_mood_data: boolean;
  share_journal_entries: boolean;
  share_check_ins: boolean;
  share_sleep_data: boolean;
  share_exercise_data: boolean;
  share_nutrition_data: boolean;
  share_therapy_sessions: boolean;
  share_assessments: boolean;
  share_goals_progress: boolean;
  share_crisis_events: boolean;
  share_ai_summaries: boolean;
}

interface ClientConsentManagementProps {
  clinicianId?: string;
  isClientView?: boolean;
}

const consentCategories = [
  {
    key: 'share_mood_data',
    label: 'Mood & Emotional Data',
    description: 'Daily mood check-ins, emotional patterns, and energy levels',
    icon: '😊',
    sensitive: false
  },
  {
    key: 'share_check_ins',
    label: 'Daily Check-ins',
    description: 'Morning and evening reflections, gratitude entries',
    icon: '📋',
    sensitive: false
  },
  {
    key: 'share_journal_entries',
    label: 'Journal Entries',
    description: 'Written reflections and therapeutic journaling',
    icon: '📝',
    sensitive: true
  },
  {
    key: 'share_sleep_data',
    label: 'Sleep Data',
    description: 'Sleep quality, duration, and patterns',
    icon: '😴',
    sensitive: false
  },
  {
    key: 'share_exercise_data',
    label: 'Exercise & Activity',
    description: 'Physical activity, exercise routines, and movement',
    icon: '🏃',
    sensitive: false
  },
  {
    key: 'share_nutrition_data',
    label: 'Nutrition Data',
    description: 'Eating habits, hydration, and nutrition tracking',
    icon: '🥗',
    sensitive: false
  },
  {
    key: 'share_therapy_sessions',
    label: 'Therapy Session Notes',
    description: 'Notes from your reflection conversations within the app',
    icon: '💬',
    sensitive: true
  },
  {
    key: 'share_assessments',
    label: 'Assessment Results',
    description: 'PHQ-9, GAD-7, and other clinical assessments',
    icon: '📊',
    sensitive: true
  },
  {
    key: 'share_goals_progress',
    label: 'Goals & Progress',
    description: 'Treatment goals, milestones, and achievements',
    icon: '🎯',
    sensitive: false
  },
  {
    key: 'share_crisis_events',
    label: 'Crisis Events',
    description: 'Safety-related events and crisis interventions',
    icon: '🚨',
    sensitive: true
  },
  {
    key: 'share_ai_summaries',
    label: 'AI-Generated Insights',
    description: 'AI summaries and pattern analysis from your data',
    icon: '🤖',
    sensitive: false
  }
];

export const ClientConsentManagement: React.FC<ClientConsentManagementProps> = ({
  clinicianId,
  isClientView = true
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [hasChanges, setHasChanges] = useState(false);

  const { data: consent, isLoading } = useQuery({
    queryKey: ['client-consent', clinicianId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('client_data_consent')
        .select('*')
        .eq('client_id', user.id)
        .eq('clinician_id', clinicianId || '')
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: isClientView && !!clinicianId
  });

  const [settings, setSettings] = useState<ConsentSettings>({
    share_mood_data: false,
    share_journal_entries: false,
    share_check_ins: false,
    share_sleep_data: false,
    share_exercise_data: false,
    share_nutrition_data: false,
    share_therapy_sessions: false,
    share_assessments: false,
    share_goals_progress: false,
    share_crisis_events: false,
    share_ai_summaries: false,
  });

  React.useEffect(() => {
    if (consent) {
      setSettings({
        share_mood_data: consent.share_mood_data,
        share_journal_entries: consent.share_journal_entries,
        share_check_ins: consent.share_check_ins,
        share_sleep_data: consent.share_sleep_data,
        share_exercise_data: consent.share_exercise_data,
        share_nutrition_data: consent.share_nutrition_data,
        share_therapy_sessions: consent.share_therapy_sessions,
        share_assessments: consent.share_assessments,
        share_goals_progress: consent.share_goals_progress,
        share_crisis_events: consent.share_crisis_events,
        share_ai_summaries: consent.share_ai_summaries,
      });
    }
  }, [consent]);

  const updateConsent = useMutation({
    mutationFn: async (newSettings: ConsentSettings) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('client_data_consent')
        .upsert({
          client_id: user.id,
          clinician_id: clinicianId,
          ...newSettings,
          last_updated_at: new Date().toISOString()
        }, {
          onConflict: 'client_id,clinician_id'
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-consent'] });
      setHasChanges(false);
      toast({
        title: 'Consent settings updated',
        description: 'Your data sharing preferences have been saved.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error saving settings',
        description: error.message || 'Please try again.',
        variant: 'destructive'
      });
    }
  });

  const handleToggle = (key: keyof ConsentSettings) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    updateConsent.mutate(settings);
  };

  const enableAll = () => {
    setSettings({
      share_mood_data: true, share_journal_entries: true, share_check_ins: true,
      share_sleep_data: true, share_exercise_data: true, share_nutrition_data: true,
      share_therapy_sessions: true, share_assessments: true, share_goals_progress: true,
      share_crisis_events: true, share_ai_summaries: true,
    });
    setHasChanges(true);
  };

  const disableAll = () => {
    setSettings({
      share_mood_data: false, share_journal_entries: false, share_check_ins: false,
      share_sleep_data: false, share_exercise_data: false, share_nutrition_data: false,
      share_therapy_sessions: false, share_assessments: false, share_goals_progress: false,
      share_crisis_events: false, share_ai_summaries: false,
    });
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <Card className="border-border/50">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/3" />
            <div className="h-20 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-primary" />
            <div>
              <CardTitle>Data Sharing Consent</CardTitle>
              <CardDescription>
                Control what information your therapist can see
              </CardDescription>
            </div>
          </div>
          <Badge variant="outline" className="text-xs">
            GDPR Compliant
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-primary mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-foreground mb-1">Your Data, Your Control</p>
              <p className="text-muted-foreground">
                You decide exactly what information to share with your therapist. 
                You can change these settings at any time, and your therapist will only 
                see data from after you've given consent.
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={enableAll}>
            <Eye className="w-4 h-4 mr-2" />
            Share All
          </Button>
          <Button variant="outline" size="sm" onClick={disableAll}>
            <EyeOff className="w-4 h-4 mr-2" />
            Hide All
          </Button>
        </div>

        <Separator />

        <div className="space-y-4">
          {consentCategories.map((category, index) => (
            <motion.div
              key={category.key}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`flex items-start justify-between p-4 rounded-lg border ${
                settings[category.key as keyof ConsentSettings]
                  ? 'bg-primary/5 border-primary/20'
                  : 'bg-muted/30 border-border/50'
              }`}
            >
              <div className="flex items-start gap-3 flex-1">
                <span className="text-2xl">{category.icon}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Label className="font-medium text-foreground">
                      {category.label}
                    </Label>
                    {category.sensitive && (
                      <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/20">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Sensitive
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {category.description}
                  </p>
                </div>
              </div>
              <Switch
                checked={settings[category.key as keyof ConsentSettings]}
                onCheckedChange={() => handleToggle(category.key as keyof ConsentSettings)}
              />
            </motion.div>
          ))}
        </div>

        {hasChanges && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="sticky bottom-0 bg-background/95 backdrop-blur border-t border-border pt-4"
          >
            <Button 
              className="w-full bg-primary hover:bg-primary/90"
              onClick={handleSave}
              disabled={updateConsent.isPending}
            >
              <Save className="w-4 h-4 mr-2" />
              {updateConsent.isPending ? 'Saving...' : 'Save Consent Settings'}
            </Button>
          </motion.div>
        )}

        <div className="bg-muted/30 rounded-lg p-4 space-y-2">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Lock className="w-4 h-4" />
            <span className="text-sm font-medium">Privacy Information</span>
          </div>
          <ul className="text-xs text-muted-foreground space-y-1 ml-6">
            <li>• Your data is encrypted and stored securely</li>
            <li>• You can revoke access at any time</li>
            <li>• Your therapist cannot access data from before you gave consent</li>
            <li>• We comply with GDPR and UK data protection regulations</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default ClientConsentManagement;
